<script>
  import { armStartupChime } from '../os/sound.svelte.js';
  import { portfolio } from '../os/portfolio.svelte.js';
  import DitherStage from './boot/DitherStage.svelte';

  const APPLE_PATH =
    'M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C72.7 141.9 32 175.6 32 244.1c0 20.2 3.7 41.1 11.1 62.6 9.9 28.3 45.6 97.7 82.8 96.6 19.4-.5 33.1-13.8 58.4-13.8 24.5 0 37.2 13.8 58.5 13.8 37.6-.5 69.9-63.7 79.4-92.1-50.4-23.8-47.9-69.7-47.9-71.5zM262.7 113.3c20.4-24.7 18.5-47.2 17.9-55.3-17.9 1-38.6 12.2-50.4 26-13 14.9-20.6 33.3-19 52.9 19.4 1.5 37.2-8.5 51.5-23.6z';

  const MASK = { path: APPLE_PATH, viewBox: [0, 0, 384, 512], fill: 0.68 };

  // Same shape as the old CSS keyframe: 0.5s hold, then 4.4s of fill.
  const DELAY_MS = 500;
  const FILL_MS = 4400;

  let progress = $state(0);

  const reduced = $derived(
    portfolio.settings.reduceMotion ||
      (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches)
  );

  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  $effect(() => {
    // Play the startup chime automatically — no prompt, no keypress.
    armStartupChime();
  });

  // One JS-driven progress value feeds both the bar and the voxel reveal, so
  // the logo materialises exactly in step with the fill.
  $effect(() => {
    if (reduced) {
      progress = 1;
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (t) => {
      const e = (t - start - DELAY_MS) / FILL_MS;
      progress = easeInOut(Math.min(1, Math.max(0, e)));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  });
</script>

<div class="splash">
  <div class="stage" aria-hidden="true">
    <DitherStage
      {progress}
      mask={MASK}
      thresholdMap="voidcluster"
      centerY={0.4}
      reducedMotion={reduced}
    />
  </div>
  <div class="vignette" aria-hidden="true"></div>

  <!-- Loader styled exactly like the OS app windows (graphite titlebar + bevel body). -->
  <div class="loader-window">
    <div class="titlebar">
      <div class="traffic" aria-hidden="true">
        <span class="tl close"></span>
        <span class="tl min"></span>
        <span class="tl zoom"></span>
      </div>
      <span class="title">Faizan's Lab</span>
    </div>

    <div class="body">
      <h1 class="loader-title extruded">Faizan's Lab</h1>
      <p class="loader-sub">Retro OS &nbsp;·&nbsp; v2.0</p>

      <div
        class="boot-progress"
        role="progressbar"
        aria-label="Starting up"
        aria-valuenow={Math.round(progress * 100)}
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <div class="boot-progress-fill" style={`width:${progress * 100}%`}></div>
      </div>
      <p class="boot-text">Starting up...</p>
    </div>
  </div>
</div>

<style>
  .splash {
    position: relative;
    height: 100vh;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding-bottom: 8vh;
    background: #0c0f0e;
    overflow: hidden;
  }

  /* full-bleed dithered voxel relief behind the loader */
  .stage {
    position: absolute;
    inset: 0;
  }
  .vignette {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(ellipse at 50% 40%, rgba(0, 0, 0, 0) 36%, rgba(4, 6, 6, 0.7) 100%),
      linear-gradient(to bottom, rgba(4, 6, 6, 0.55), rgba(4, 6, 6, 0) 24%, rgba(4, 6, 6, 0.6));
  }

  /* --- window container, matching the desktop app windows --- */
  .loader-window {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    min-width: 360px;
    background: var(--window-bg, #545e5d);
    border: 2px solid;
    border-color: var(--bevel-light, #8b9694) var(--bevel-dark, #1a201f) var(--bevel-dark, #1a201f)
      var(--bevel-light, #8b9694);
    outline: 1px solid var(--bevel-outline, #000);
    box-shadow:
      inset 1px 1px 0 rgba(255, 255, 255, 0.2),
      inset -1px -1px 0 rgba(0, 0, 0, 0.3),
      0 18px 42px rgba(0, 0, 0, 0.72),
      0 2px 7px rgba(0, 0, 0, 0.5);
    animation: win-in 0.55s cubic-bezier(0.2, 1.3, 0.4, 1) both;
  }
  @keyframes win-in {
    from {
      opacity: 0;
      transform: scale(0.92) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .titlebar {
    position: relative;
    display: flex;
    align-items: center;
    height: 22px;
    padding: 0 7px;
    background:
      repeating-linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.035) 0 1px,
        rgba(0, 0, 0, 0.045) 1px 2px
      ),
      linear-gradient(to bottom, #626969 0%, #343a3a 100%);
    border-bottom: 2px solid #10100f;
    box-shadow:
      inset 0 2px 0 rgba(255, 255, 255, 0.22),
      inset 0 -2px 0 rgba(0, 0, 0, 0.3);
  }
  .traffic {
    display: flex;
    gap: 6px;
    align-items: center;
    z-index: 1;
  }
  .tl {
    width: 13px;
    height: 13px;
    border: 1px solid rgba(0, 0, 0, 0.32);
    border-radius: 50%;
  }
  .tl.close {
    background: linear-gradient(to bottom, #ff7b72, #d9403b);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.28),
      0 1px 1px rgba(0, 0, 0, 0.36);
  }
  .tl.min {
    background: linear-gradient(to bottom, #ffd264, #d99a1f);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.28),
      0 1px 1px rgba(0, 0, 0, 0.36);
  }
  .tl.zoom {
    background: linear-gradient(to bottom, #6ee17b, #24a83e);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.28),
      0 1px 1px rgba(0, 0, 0, 0.36);
  }
  .title {
    position: absolute;
    inset: 0 76px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: #f1f4f7;
    white-space: nowrap;
  }

  .body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 24px 46px 22px;
  }

  /* extruded logotype, same language as the Welcome hero */
  .extruded {
    margin: 0 0 2px;
    font: 900 26px var(--font);
    letter-spacing: 1px;
    color: #ffd85a;
    -webkit-text-stroke: 1px #000;
    /* outline under the fill, or thin fallback fonts (Windows) turn dark */
    paint-order: stroke fill;
    text-shadow:
      1px 1px 0 #000,
      2px 2px 0 #2a3130,
      3px 3px 0 #232928,
      4px 4px 0 #1b201f,
      5px 5px 6px rgba(0, 0, 0, 0.6);
  }
  .loader-sub {
    margin: 6px 0 16px;
    color: #b6bfc2;
    font: 11px var(--font);
    letter-spacing: 0.6px;
    text-shadow: 0 1px 0 rgba(0, 0, 0, 0.55);
  }

  /* sleek black progress bar with an animate-ui-style light sweep */
  .boot-progress {
    position: relative;
    width: 250px;
    height: 14px;
    background: #050506;
    border: 2px solid;
    border-color: #000 #3a3a3a #3a3a3a #000;
    outline: 1px solid #000;
    box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.9);
    padding: 2px;
    overflow: hidden;
  }
  .boot-progress-fill {
    position: relative;
    height: 100%;
    width: 0;
    background: linear-gradient(to bottom, #d9c34a 0%, #a68f26 46%, #5f5015 100%);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.34),
      inset 0 -1px 2px rgba(0, 0, 0, 0.6);
    overflow: hidden;
  }
  /* moving shine that travels across the filled portion */
  .boot-progress-fill::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      100deg,
      transparent 0%,
      rgba(255, 255, 255, 0.26) 45%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0.26) 55%,
      transparent 100%
    );
    transform: translateX(-100%);
    animation: boot-sheen 1.5s ease-in-out 0.6s infinite;
  }
  @keyframes boot-sheen {
    0% {
      transform: translateX(-100%);
    }
    60%,
    100% {
      transform: translateX(240%);
    }
  }

  .boot-text {
    margin: 8px 0 0;
    color: #c3cccf;
    font: 12px var(--font);
    text-shadow: 0 1px 0 rgba(0, 0, 0, 0.55);
  }

  @media (prefers-reduced-motion: reduce) {
    .loader-window {
      animation-duration: 0.001ms;
    }
    .boot-progress-fill::after {
      animation: none;
    }
  }
</style>
